import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { enGB, ro as roDateFns, srLatn } from 'date-fns/locale';
import en from './locales/en.json';
import ro from './locales/ro.json';
import sr from './locales/sr.json';

// `intl` is the BCP 47 tag for Intl / toLocaleDateString (Serbian is written in
// Latin script in this app — plain 'sr-RS' would print Cyrillic month names);
// `dateFns` is the matching date-fns locale for format() and react-datepicker.
export const SUPPORTED_LANGUAGES = [
    { code: 'en', label: 'English', intl: 'en-GB', dateFns: enGB },
    { code: 'ro', label: 'Română', intl: 'ro-RO', dateFns: roDateFns },
    { code: 'sr', label: 'Srpski', intl: 'sr-Latn-RS', dateFns: srLatn },
];

const currentLanguage = () =>
    SUPPORTED_LANGUAGES.find(l => l.code === (i18n.resolvedLanguage || i18n.language)) || SUPPORTED_LANGUAGES[0];

/** Locale tag for toLocaleDateString / Intl.DateTimeFormat in the active language. */
export const dateLocale = () => currentLanguage().intl;

/** date-fns locale object for format() in the active language. */
export const dateFnsLocale = () => currentLanguage().dateFns;

// Romanian and Serbian write month/day names in lower case; these lists label
// headers, axes and dropdowns on their own, so start them with a capital.
const capitalize = (s) => s.charAt(0).toLocaleUpperCase(dateLocale()) + s.slice(1);

/** Month names in the active language, January first. `style`: 'long' | 'short'. */
export const monthNames = (style = 'long') => {
    const fmt = new Intl.DateTimeFormat(dateLocale(), { month: style, timeZone: 'UTC' });
    return Array.from({ length: 12 }, (_, m) => capitalize(fmt.format(new Date(Date.UTC(2021, m, 1)))));
};

/** Weekday names in the active language, Sunday first (same order as Date#getDay). */
export const weekdayNames = (style = 'long') => {
    const fmt = new Intl.DateTimeFormat(dateLocale(), { weekday: style, timeZone: 'UTC' });
    // 3 January 2021 was a Sunday.
    return Array.from({ length: 7 }, (_, d) => capitalize(fmt.format(new Date(Date.UTC(2021, 0, 3 + d)))));
};

SUPPORTED_LANGUAGES.forEach(({ code, dateFns }) => registerLocale(code, dateFns));

// Keep the date pickers' month/day names and the page's lang attribute in step
// with the UI language.
i18n.on('languageChanged', () => {
    const { code } = currentLanguage();
    setDefaultLocale(code);
    if (typeof document !== 'undefined') document.documentElement.lang = code;
});

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: { en: { translation: en }, ro: { translation: ro }, sr: { translation: sr } },
        supportedLngs: SUPPORTED_LANGUAGES.map(l => l.code),
        fallbackLng: 'en',
        interpolation: { escapeValue: false },
        detection: {
            order: ['localStorage', 'navigator'],
            lookupLocalStorage: 'soroby-locale',
            caches: ['localStorage'],
            // Browsers report 'en-US', 'sr-Latn-RS', … — the app only knows the
            // base language, and components compare i18n.language to it.
            convertDetectedLanguage: (lng) => lng.split('-')[0],
        },
    });

export default i18n;
